/**
 * Durable "which agent is this client working with" context for onboarding.
 *
 * The agent invite link (/join?ref=TOKEN) resolves the agent before the client
 * has even registered a passkey. We stash that context so it survives the
 * signup flow AND any exploring the client does in the SPA before they finish
 * onboarding — so we never lose track of their agent. Cleared once the deal
 * is opened.
 *
 * localStorage, NOT sessionStorage (Checkpoint #16 live finding): signup's
 * email-verification step (/verify-email?token=…) is reached by clicking a
 * link in the client's inbox, which very commonly opens in a NEW TAB —
 * sessionStorage is scoped per-tab and would silently lose the agent context
 * there, landing the client on a generic (non-dual, agent-unknown) onboarding
 * wizard despite having followed a specific agent's link. localStorage
 * survives across tabs on the same browser; it does not survive a genuinely
 * different device (e.g. checking email on a phone after starting on a
 * laptop) — that residual gap needs the agent context threaded through the
 * signup token server-side to close completely.
 */

const KEY = "rp.onboarding_agent";
const LEGACY_REF_KEY = "rp.agent_ref"; // older ?ref=<agent_id> path

export interface OnboardingAgent {
  agentId: string;
  agentTenantId?: string;
  agentName?: string;
  agentPhone?: string | null;
  token?: string;
  /**
   * Spec 023 — the agent's referral token from `/join?ref=TOKEN`. The ONE place the
   * browser carries it: sign-up sends it as `invite_token` so the server can record
   * which invite the account came from. Kept only while the visitor stays in the
   * invite's brokerage (`agentTenantId`) — see `inviteTokenFor`.
   */
  inviteToken?: string;
}

export function saveOnboardingAgent(agent: OnboardingAgent): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(agent));
  } catch {
    // storage unavailable (private mode quota) — degrade gracefully.
  }
}

export function loadOnboardingAgent(): OnboardingAgent | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as OnboardingAgent;
    const legacy = localStorage.getItem(LEGACY_REF_KEY);
    if (legacy) return { agentId: legacy };
  } catch {
    // ignore parse/storage errors — treated as "no agent context".
  }
  return null;
}

/**
 * The stored invite token, but only for the brokerage it came from. A token from
 * another brokerage must never travel with a sign-up here: the server would let the
 * invite decide the brokerage. Spec 023.
 */
export function inviteTokenFor(
  tenantId: string | undefined,
): string | undefined {
  const stored = loadOnboardingAgent();
  if (!stored?.inviteToken || !tenantId) return undefined;
  return stored.agentTenantId === tenantId ? stored.inviteToken : undefined;
}

export function clearOnboardingAgent(): void {
  try {
    localStorage.removeItem(KEY);
    localStorage.removeItem(LEGACY_REF_KEY);
  } catch {
    // ignore
  }
}
